import type { KeycloakInstance, KeycloakTokenParsed } from 'keycloak-js';

export interface DesktopAuthSession {
  authenticated: boolean;
  accessToken: string | null;
  tokenParsed: KeycloakTokenParsed | null;
  realm: string;
}

export type DesktopLaunchIntent = 'full' | 'word' | 'update';
export type DesktopUpdateStatus = 'idle' | 'checking' | 'up-to-date' | 'downloading' | 'ready' | 'installing' | 'failed';

export interface DesktopUpdateState {
  status: DesktopUpdateStatus;
  configured: boolean;
  channel: 'stable' | 'pilot' | 'manual';
  channelSource: 'default' | 'policy' | 'environment';
  currentVersion: string;
  availableVersion: string | null;
  releaseName: string | null;
  releaseNotes: string | null;
  lastCheckedAt: string | null;
  deferredAt: string | null;
  errorCode: string | null;
}

export interface DesktopApplication {
  id: string;
  label: string;
  route: string;
  capabilities: string[];
  desktopStatus: 'included' | 'unsupported';
  roleRestricted?: boolean;
}

export interface DesktopBridge {
  runtime: { get: () => Promise<{ apiOrigin: string; realm: string; version: string; platform: string; packaged: boolean; launchIntent: DesktopLaunchIntent }> };
  applications: { list: () => Promise<DesktopApplication[]> };
  auth: {
    session: () => Promise<DesktopAuthSession>;
    login: (realm: string) => Promise<DesktopAuthSession>;
    refresh: () => Promise<DesktopAuthSession>;
    logout: () => Promise<DesktopAuthSession>;
  };
  word: {
    openWorkingCopy: (payload: { bytes: ArrayBuffer; filename: string }) => Promise<{ filename: string }>;
  };
  updates: {
    getState: () => Promise<DesktopUpdateState>;
    check: () => Promise<DesktopUpdateState>;
    defer: () => Promise<DesktopUpdateState>;
    installAndRestart: () => Promise<DesktopUpdateState>;
    onStateChange: (callback: (state: DesktopUpdateState) => void) => () => void;
  };
}

declare global {
  interface Window { edgelexDesktop?: DesktopBridge; }
}

export const isDesktopRuntime = (): boolean => Boolean(window.edgelexDesktop?.auth);

export const desktopTokenHasRealmRole = (
  tokenParsed: KeycloakTokenParsed | null | undefined,
  role: string,
): boolean => {
  const realmRoles = tokenParsed?.realm_access?.roles;
  return Array.isArray(realmRoles) && realmRoles.includes(role);
};

const DESKTOP_APP_ORIGIN = 'app://edgelex';
const CLOUD_API_ORIGIN = 'https://lex.edgelex.com';
let desktopTransportInstalled = false;

/**
 * Keep browser production URLs intact while routing Electron API fetches
 * through the authenticated main-process proxy. Axios relative URLs already
 * use app:// directly; this covers legacy fetch clients with absolute cloud
 * URLs without persisting credentials in renderer storage.
 */
export const installDesktopTransportAdapter = (): void => {
  if (!isDesktopRuntime() || desktopTransportInstalled) return;
  desktopTransportInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const originalUrl = input instanceof Request ? input.url : String(input);
    let rewrittenUrl = originalUrl;
    try {
      const parsed = new URL(originalUrl, window.location.href);
      if (parsed.origin === CLOUD_API_ORIGIN && parsed.pathname.startsWith('/api/')) {
        rewrittenUrl = `${DESKTOP_APP_ORIGIN}${parsed.pathname}${parsed.search}`;
      }
    } catch {
      // Native fetch retains responsibility for invalid URL reporting.
    }
    if (input instanceof Request && rewrittenUrl !== originalUrl) {
      return nativeFetch(new Request(rewrittenUrl, input), init);
    }
    return nativeFetch(rewrittenUrl === originalUrl ? input : rewrittenUrl, init);
  };
};

export const desktopSocketNamespace = (namespace: string): string => {
  const normalized = namespace.startsWith('/') ? namespace : `/${namespace}`;
  return isDesktopRuntime() ? `${CLOUD_API_ORIGIN}${normalized}` : normalized;
};

export class DesktopKeycloakClient {
  authenticated = false;
  token?: string;
  // @react-keycloak/core defines authenticated as truthy idToken + token.
  // Native desktop auth validates the ID token in the main process and does
  // not expose it to the renderer, so this non-credential marker represents
  // that completed validation without duplicating credential material.
  idToken?: string;
  tokenParsed?: KeycloakTokenParsed;
  realm = 'corp';
  subject?: string;
  onReady?: (authenticated?: boolean) => void;
  onAuthSuccess?: () => void;
  onAuthError?: (errorData?: unknown) => void;
  onAuthRefreshSuccess?: () => void;
  onAuthRefreshError?: () => void;
  onAuthLogout?: () => void;
  onTokenExpired?: () => void;
  private expiryTimer?: number;

  private apply(session: DesktopAuthSession): boolean {
    this.authenticated = session.authenticated;
    this.token = session.accessToken || undefined;
    this.idToken = session.authenticated ? 'edgelex-desktop-validated-session' : undefined;
    this.tokenParsed = session.tokenParsed || undefined;
    this.realm = session.realm;
    this.subject = session.tokenParsed?.sub;
    if (this.expiryTimer) window.clearTimeout(this.expiryTimer);
    const expiresAt = Number(session.tokenParsed?.exp || 0) * 1000;
    if (expiresAt > Date.now()) {
      this.expiryTimer = window.setTimeout(() => this.onTokenExpired?.(), Math.max(0, expiresAt - Date.now() - 30000));
    }
    return this.authenticated;
  }

  async init(): Promise<boolean> {
    try {
      const authenticated = this.apply(await window.edgelexDesktop!.auth.session());
      this.onReady?.(authenticated);
      if (authenticated) this.onAuthSuccess?.();
      return authenticated;
    } catch (error) { this.onAuthError?.(error); throw error; }
  }

  async login(): Promise<void> {
    try { this.apply(await window.edgelexDesktop!.auth.login('corp')); this.onAuthSuccess?.(); }
    catch (error) { this.onAuthError?.(error); throw error; }
  }

  async updateToken(): Promise<boolean> {
    try { const changed = this.apply(await window.edgelexDesktop!.auth.refresh()); this.onAuthRefreshSuccess?.(); return changed; }
    catch (error) { this.onAuthRefreshError?.(); throw error; }
  }

  async logout(): Promise<void> {
    await window.edgelexDesktop!.auth.logout();
    this.apply({ authenticated: false, accessToken: null, tokenParsed: null, realm: 'corp' });
    this.onAuthLogout?.();
  }

  clearToken(): void {
    this.token = undefined;
    this.idToken = undefined;
    this.tokenParsed = undefined;
    this.authenticated = false;
  }

  hasRealmRole(role: string): boolean {
    return desktopTokenHasRealmRole(this.tokenParsed, role);
  }
}

let cachedDesktopClient: KeycloakInstance | null = null;
export const getDesktopKeycloakClient = (): KeycloakInstance => {
  if (!cachedDesktopClient) cachedDesktopClient = new DesktopKeycloakClient() as unknown as KeycloakInstance;
  return cachedDesktopClient;
};
